import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DataList from "../../../components/DataList";
import BookingService from "../../../models/BookingService";
import { useStateContext } from "../../../reducers";
import { DataFormFieldChangeParamType, DefaultMenuTypes, NotificationType, PageFilterSendType, PageFilterType, PageGenericParamType } from "../../../utils/Types";
import PageContainer from "../../../components/PageContainer";
import NotificationDialog from "../../../components/NotificationDialog";
import Loader from "../../../components/Loader";
import { useNavigate } from 'react-router-dom';
import {
    Add as AddIcon,
    FileDownload as FileDownloadIcon,
	FileUpload as FileUploadIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Build as CreateIcon,
    ManageAccounts as ManageAccountsIcon,
    Cancel as CancelIcon,
    Sort as SortIcon,
    SwapVert as SwapVertIcon,
} from '@mui/icons-material';
import { PageContainerActionParamType, SORT_ASC, SORT_DESC, STATUS_BOOK_SERV_CANCELLED, STATUS_BOOK_SERV_COMPLETED, STATUS_BOOK_SERV_NEW, getPathObject } from "../../../utils/Common";
import { MenuCode, MenuGroupName, MenuTitle } from "../../../utils/Menu";
import { AuthMenuType } from "../../../reducers/auth";
import { FlippedSortIcon } from "../../../styles";

const groupName: MenuGroupName = "Servis";
const subgroupName: string = "Transaksi";
const title: MenuTitle = "Booking Service";
const code: MenuCode = "SERVICE-BOOKING";
const endpoint: string = "book";

function pathObject(): (PageContainerActionParamType | null) {
    return getPathObject(groupName, subgroupName, code);
};

function List() {
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [error, setError] = useState<NotificationType | null>(null);
    const [success, setSuccess] = useState<NotificationType | null>(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState<PageFilterSendType[]>([]);
    const [sort, setSort] = useState<PageGenericParamType | undefined>(undefined);
    const [shouldFilterData, setShouldFilterData] = useState(false);
    const [dataToEdit, setDataToEdit] = useState<BookingService | null>(null);
    const [dataToDelete, setDataToDelete] = useState<BookingService | null>(null);
    const [dataToView, setDataToView] = useState<BookingService | null>(null);
    const [dataToWorkorder, setDataToWorkorder] = useState<BookingService | null>(null);
    const [dataToGetWorkorder, setDataToGetWorkorder] = useState<BookingService | null>(null);
    const [dataToCancel, setDataToCancel] = useState<BookingService | null>(null);
    const [needReload, setNeedReload] = useState(false);
    const [forceCloseFilterWindow, setForceCloseFilterWindow] = useState(false);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageCount, setPageCount] = useState<number>(1);
    const filtersRef = useRef<PageFilterSendType[]>([]);
	const navigate = useNavigate();

    const menuAuthState: AuthMenuType | undefined = useMemo(() => {
        return authState?.daftar_menu?.filter(m => m.kode_menu === code)[0];
    }, [authState]);

    useEffect(() => {
        setNeedReload(true);
    }, []);

    useEffect(() => {
        if (!needReload)
            return;

        setNeedReload(false);
    }, [needReload]);
    
    const handleFilterChanged = useCallback((filter: DataFormFieldChangeParamType<any>) => {
        const currentFilter = filtersRef.current.filter((f) => f.name === filter.fieldName)[0];

        if (!currentFilter)
        {
            if (!(filter.data as any)[filter.fieldName])
                return;

            filtersRef.current.push({
                name: filter.fieldName,
                value: (filter.data as any)[filter.fieldName],
            })
            return;
        }

        const currentFilterIndex = filtersRef.current.indexOf(currentFilter);

        if (!(filter.data as any)[filter.fieldName])
            filtersRef.current.splice(currentFilterIndex, 1);
        
        if ((filter.data as any)[filter.fieldName])
            filtersRef.current.splice(currentFilterIndex, 1, {name: filter.fieldName, value: (filter.data as any)[filter.fieldName]});
    }, [filtersRef.current]);

    const handleSortChanged = useCallback((key: string) => {
        const newSort: PageGenericParamType = sort ? JSON.parse(JSON.stringify(sort)) : ({name: key});

        if (!newSort.value)
        {
            newSort.value = SORT_ASC;
            setSort(newSort);
            setNeedReload(true);
            return;
        }

        if (newSort.value === SORT_ASC)
        {
            newSort.value = SORT_DESC;
            setSort(newSort);
            setNeedReload(true);
            return;
        }

        if (newSort.value === SORT_DESC)
        {
            setSort(undefined);
            setNeedReload(true);
            return;
        }
    }, [sort]);

	const handleAddClick = (pathObject: any) => {
		navigate(`${pathObject.mainPath}${pathObject.formPath}`, {state: {}});
	};

	const handleEditClick = useCallback((pathObject: any) => {
        if (!dataToEdit)
            return;

		navigate(`${pathObject.mainPath}${pathObject.formPath}`, {state: {...dataToEdit}});
    }, [navigate, dataToEdit]);

    useEffect(() => {
        if (!dataToEdit)
            return;

        handleEditClick(pathObject());
    }, [dataToEdit]);

	const handleViewClick = useCallback((pathObject: any) => {
        if (!dataToView)
            return;

		navigate(`${pathObject.mainPath}${pathObject.formPath}`, {state: {...dataToView, isViewMode: true}});
    }, [navigate, dataToView]);

    useEffect(() => {
        if (!dataToView)
            return;

        handleViewClick(pathObject());
    }, [dataToView]);

	const handleGenerateWorkorderClick = useCallback(() => {
        if (!dataToWorkorder || !apiCaller)
            return;

        setLoading(true);

        apiCaller.put(`${endpoint}/change-status?ucode=${dataToWorkorder.ucode}`)
            .then(() => {
                setLoading(false);
                setDataToWorkorder(null);
                setSuccess({
                    title: "Sukses!",
                    message: "Generate workorder telah selesai dilakukan.",
                });
                setNeedReload(true);
            })
            .catch((error) => {
                console.error({error});
                setDataToWorkorder(null);
                setError({
                    title: "Error!",
                    message: "Generate workorder gagal dilakukan. Silakan hubungi administrator.",
                });
                setLoading(false);
            })
        ;
    }, [apiCaller, dataToWorkorder]);

	const handleEditWorkorderClick = useCallback((pathObject: any) => {
        if (!dataToGetWorkorder)
            return;

		navigate(`${pathObject.mainPath}${pathObject.formPath}`, {state: {...dataToGetWorkorder}});
    }, [navigate, dataToGetWorkorder]);
    
    useEffect(() => {
        if (!dataToGetWorkorder)
            return;
        
        handleEditWorkorderClick(getPathObject(groupName, subgroupName, "SERVICE-BOOKING-WO"));
    }, [dataToGetWorkorder]);

	const handleCancelClick = useCallback(() => {
        if (!dataToCancel || !apiCaller)
            return;

        setLoading(true);

        apiCaller.put(`${endpoint}/cancel-booking?ucode=${dataToCancel.ucode}`)
            .then(() => {
                setLoading(false);
                setDataToCancel(null);
                setSuccess({
                    title: "Sukses!",
                    message: "Pembatalan booking service telah selesai dilakukan.",
                });
                setNeedReload(true);
            })
            .catch((error) => {
                console.error({error});
                setDataToCancel(null);
                setError({
                    title: "Error!",
                    message: "Pembatalan booking service gagal dilakukan. Silakan hubungi administrator.",
                });
                setLoading(false);
            })
        ;
    }, [apiCaller, dataToCancel]);

	const handleDeleteClick = useCallback(() => {
        if (!dataToDelete || !apiCaller)
            return;

        setLoading(true);

        apiCaller.delete(`${endpoint}/${dataToDelete.ucode}`)
            .then(() => {
                setDataToDelete(null);
                setLoading(false);
                setNeedReload(true);
            })
            .catch((error) => {
                console.error({error});
                setError({
                    title: "Error!",
                    message: "Penghapusan data gagal dilakukan. Silakan hubungi administrator.",
                });
                setLoading(false);
            })
        ;
    }, [apiCaller, dataToDelete]);

    return (
		<>
        {
            loading && <Loader />
        }
            <PageContainer
                groupName={groupName}
                subgroupName={subgroupName}
                code={code}
                title={title}
                toolbars={[
                    {
                        id: "add",
                        title: "Tambah Booking Service",
                        icon: AddIcon,
                        action: (pathObject) => handleAddClick(pathObject),
                        disabled: !(menuAuthState?.can_add ?? false),
                    },
                    {
                        id: "exportExcel",
                        title: "Download Excel",
                        icon: FileDownloadIcon,
                        action: () => {},
                        color: "dark",
                    },
                    {
                        id: "importExcel",
                        title: "Upload",
                        icon: FileUploadIcon,
                        action: () => {},
                        color: "dark",
                    },					
                ]}
                filter={{
                    fields: [
                        {
                            fieldName: "no_transaksi",
                            fieldOperator: "contains",
                            fieldType: "string",
                            title: "No. Transaksi",
                            inputType: "text",
                            gridSizeXl: 12,
                            gridSizeLg: 12,
                            gridSizeMd: 12,
                            onChanged: handleFilterChanged,
                            canSort: true,
                            titleHasCommands: true,
                            titleCommands: [
                                {
                                    title: "Urut No. Transaksi",
                                    action: () => handleSortChanged("sort-no_transaksi"),
                                    icon: sort?.name === "sort-no_transaksi" ? (sort?.value === SORT_DESC ? SortIcon : FlippedSortIcon) : SwapVertIcon,
                                    id: "sort-no_transaksi",
                                    variant: sort?.name === "sort-no_transaksi" ? "contained" : "outlined",
                                },
                            ]
                        },
                        {
                            fieldName: "nama_customer",
                            fieldOperator: "contains",
                            fieldType: "string",
                            title: "Customer",
                            inputType: "text",
                            gridSizeXl: 12,
                            gridSizeLg: 12,
                            gridSizeMd: 12,
                            onChanged: handleFilterChanged,
                            canSort: true,
                            titleHasCommands: true,
                            titleCommands: [
                                {
                                    title: "Urut Nama Customer",
                                    action: () => handleSortChanged("sort-nama_customer"),
                                    icon: sort?.name === "sort-nama_customer" ? (sort?.value === SORT_DESC ? SortIcon : FlippedSortIcon) : SwapVertIcon,
                                    id: "sort-nama_customer",
                                    variant: sort?.name === "sort-nama_customer" ? "contained" : "outlined",
                                },
                            ]
                        },
                        {
                            fieldName: "nopol",
                            fieldOperator: "contains",
                            fieldType: "string",
                            title: "No. Polisi",
                            inputType: "text",
                            gridSizeXl: 12,
                            gridSizeLg: 12,
                            gridSizeMd: 12,
                            onChanged: handleFilterChanged,
                        },
                    ],
                    saveActionIcon: SearchIcon,
                    saveActionCaption: "Cari",
                    defaultFilters: filters,
                }}
                customFormCommands={[
                    {
                        title: "Update Filter",
                        action: () => {
                            setFilters(filtersRef.current);
                            setShouldFilterData(true);
                            setNeedReload(true);
                        },
                        icon: SearchIcon,
                        color: "primary",
                    },
                    {
                        title: "Hapus Filter",
                        action: () => {
                            setFilters([]);
                            setShouldFilterData(false);
                            setNeedReload(true);
                        },
                        icon: DeleteIcon,
                    }
                ]}
                onSetPageNumber={(value: number | undefined) => setPageNumber(value ?? 1)}
                pageCount={pageCount}
            >
                <DataList<BookingService>
                    dataType={BookingService}
                    keyField="ucode"
					setError={(errorState) => setError(errorState ?? null)}
                    setLoading={(loadingState) => setLoading(loadingState ?? false)}
                    actions={[
                        {
                            id: "Edit" as DefaultMenuTypes,
                            title: "Ubah",
                            action: (item) => setDataToEdit(item),
                            icon: EditIcon,
                            color: "primary",
                            disabled: (rowData?: BookingService) => {
                                return rowData?.kode_status !== STATUS_BOOK_SERV_NEW || !(menuAuthState?.can_edit ?? false);
                            },
                        },
                        {
                            id: "View" as DefaultMenuTypes,
                            title: "Lihat",
                            action: (item) => setDataToView(item),
                            icon: SearchIcon,
                            color: "dark",
                            disabled: (rowData?: BookingService) => {
                                return !(menuAuthState?.can_view ?? false);
                            },
                        },
                        {
                            id: "GenerateWorkorder" as DefaultMenuTypes,
                            title: "Generate Workorder",
                            action: (item) => setDataToWorkorder(item),
                            icon: CreateIcon,
                            color: "success",
                            disabled: (rowData?: BookingService) => {
                                return (rowData?.workorder_tersedia ?? false) || rowData?.kode_status !== STATUS_BOOK_SERV_NEW;
                            },
                        },
                        {
                            id: "ManageWorkorder" as DefaultMenuTypes,
                            title: "Manage Workorder",
                            action: (item) => setDataToGetWorkorder(item),
                            icon: ManageAccountsIcon,
                            color: "success",
                            disabled: (rowData?: BookingService) => {
                                return !(rowData?.workorder_tersedia && rowData?.kode_status !== STATUS_BOOK_SERV_CANCELLED);
                            },
                        },
                        {
                            id: "Batal" as DefaultMenuTypes,
                            title: "Batalkan",
                            action: (item) => setDataToCancel(item),
                            icon: CancelIcon,
                            color: "dark",
                            disabled: (rowData?: BookingService) => {
                                return !(rowData?.kode_status !== STATUS_BOOK_SERV_NEW && rowData?.kode_status !== STATUS_BOOK_SERV_COMPLETED && rowData?.kode_status !== STATUS_BOOK_SERV_CANCELLED) || !(menuAuthState?.can_edit ?? false);
                            },
                        },
                        {
                            id: "Delete" as DefaultMenuTypes,
                            title: "Hapus",
                            action: (item) => setDataToDelete(item),
                            icon: DeleteIcon,
                            color: "delete",
                            disabled: (rowData?: BookingService) => {
                                return !(menuAuthState?.can_delete ?? false);
                            },
                        },
                    ]}
                    apiCaller={apiCaller}
                    endpoint={endpoint}
                    filters={shouldFilterData ? filters : []}
                    sort={sort}
                    titleColumnName="no_transaksi"
                    needReload={needReload}
                    details={[
                        {
                            fieldName: "tanggal_transaksi",
                            title: "Tanggal",
                            fieldType: "date",
                        },
                        {
                            fieldName: "nama_customer",
                            title: "Customer",
                        },
                        {
                            fieldName: "nama_divisi",
                            title: "Divisi",
                        },
                        {
                            fieldName: "nama_status",
                            title: "Status",
                        },
                        {
                            fieldName: "keterangan_operator",
                            title: "Keterangan",
                        }
                    ]}
                    pageNumber={pageNumber}
                    onSetPageCount={(value: number | undefined) => setPageCount(value ?? 1)}
                />
            </PageContainer>
        {
            dataToWorkorder ?
            <NotificationDialog
                message={`Generate workorder untuk Booking Service "${dataToWorkorder.no_transaksi}"?`}
                onClose={() => setDataToWorkorder(null)}
                onConfirm={() => handleGenerateWorkorderClick()}
                title="Konfirmasi"
                confirmButtonText="Ya"
                closeButtonText="Batal"
                confirm
            />
            :
            null
        }
        {
            dataToCancel ?
            <NotificationDialog
                message={`Apakah Anda yakin akan membatalkan booking service "${dataToCancel.no_transaksi}"?`}
                onClose={() => setDataToCancel(null)}
                onConfirm={() => handleCancelClick()}
                title="Konfirmasi"
                confirmButtonText="Ya"
                closeButtonText="Batal"
                warning
            />
            :
            null
        }
        {
            dataToDelete ?
            <NotificationDialog
                message={`Hapus data "${dataToDelete.no_transaksi}"?`}
                onClose={() => setDataToDelete(null)}
                onConfirm={() => handleDeleteClick()}
                title="Konfirmasi"
                confirmButtonText="Ya"
                closeButtonText="Batal"
                warning
            />
            :
            null
        }
        {
            error ?
            <NotificationDialog
                message={error.message}
                onClose={() => setError(null)}
                title={error.title}
                closeButtonText="Tutup"
                warning
            />
            :
            null
        }
		</>
    );
};

export default List;